import { EventCategoryResponse, EventRequirements, PoiCategoryResponse, PoiRequirements, PoiResponse } from "../../interfaces";
import { getData } from "./base.utils";

export const getPois = (baseUrl: string, category?: string): Promise<PoiResponse[]> => {
    const path = category ? `/pois?filter={"pointOfInterestCategoryId":"${category}"}`: '/pois';
    return getData<PoiResponse[]>(baseUrl, path);
};

export const getEvents = (baseUrl: string): Promise<PoiResponse[]> => {
    return getData<PoiResponse[]>(baseUrl, '/events');
};

export const getEventCategories = (baseUrl: string): Promise<EventCategoryResponse[]> => {
    return getData<EventCategoryResponse[]>(baseUrl, '/events/categories');
};

const hasCoordinates = (item: PoiResponse): boolean =>
    item.longitude != null && item.latitude != null &&
    Number.isFinite(Number(item.longitude)) && Number.isFinite(Number(item.latitude));

// Events linked to a POI may be saved without their own position; use the linked POI's position.
export const withLinkedPoiPosition = (event: PoiResponse, pois: PoiResponse[]): PoiResponse => {
    if (hasCoordinates(event)) {
        return event;
    }
    const linkedPoiId = event.pointOfInterestId;
    const linkedPoi = linkedPoiId != null ? pois.find((poi) => String(poi.id) === String(linkedPoiId)) : undefined;
    if (!linkedPoi || !hasCoordinates(linkedPoi)) {
        return event;
    }
    return {
        ...event,
        longitude: linkedPoi.longitude,
        latitude: linkedPoi.latitude,
        elevation: event.elevation ?? linkedPoi.elevation,
        // Lets the map group the event next to its POI instead of stacking it on top.
        positionFromPoi: true,
    } as PoiResponse;
};

/**
 * Gets events (as POI-shaped responses) and their categories. Pass the POIs so that events
 * linked to a POI without an own position can borrow the POI's position.
 */
export const getEventRequirements = (baseUrl: string, pois: PoiResponse[] = []): Promise<EventRequirements> => {
    return Promise.all([
        getEvents(baseUrl),
        getEventCategories(baseUrl),
    ]).then(([events, categories]) => ({
        events: (events ?? [])
            .map((event) => ({
                ...event,
                eventCategoryId: event.eventCategoryId ?? event.pointOfInterestCategoryId,
            }))
            .filter((event) => event.eventCategoryId != null)
            .map((event) => withLinkedPoiPosition(event, pois)),
        categories: categories ?? [],
    }));
};

export const getPoiCategories = (baseUrl: string): Promise<PoiCategoryResponse[]> => {
    const path = '/pois/categories';
    return getData<PoiCategoryResponse[]>(baseUrl, path);
};

/**
 * Gets the required data to render the pois, alternatively filter by category
 */
 export const getPoiRequirements = (baseUrl: string, category?: string): Promise<PoiRequirements> => {
    return new Promise<PoiRequirements>((resolve, reject) => {
        Promise.all([
            getPois(baseUrl, category),
            getPoiCategories(baseUrl),
        ]).then((response: [PoiResponse[], PoiCategoryResponse[]]) => {
            const [pois, categories] = response;

            resolve({pois, categories});
        }).catch((error) => reject(error));
    });
};